import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Car,
  Ship,
  Waves,
  MapPin,
  Clock,
  ExternalLink,
  ArrowRight,
  Compass,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/Button';

const PIN_LAT = 14.758087;
const PIN_LNG = 120.917185;
const MAP_LINK = `https://www.google.com/maps/search/?api=1&query=${PIN_LAT},${PIN_LNG}`;
const EMBED_URL = `https://www.google.com/maps?q=${PIN_LAT},${PIN_LNG}&z=13&output=embed`;

const heroBg =
  'https://images.unsplash.com/photo-1776135933174-7465a18e98d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxiYW1ib28lMjBodXQlMjByZXNvcnQlMjBuYXR1cmV8ZW58MHx8fHwxNzgxMzI2OTkzfDA&ixlib=rb-4.1.0&q=85';

const routes = [
  {
    icon: Car,
    badge: 'Fast & easy',
    duration: 'Direct land journey',
    title: 'Drive Straight into Your Fishing Escape',
    eyebrow: 'Option 1 · By car via Sitio Asana New Bypass Road',
    body: `For guests who want the most direct and flexible route, arriving by car is the easiest way to reach Asana. The drive brings you closer and closer to the quieter edge of Bulacan, where the atmosphere gradually shifts from busy roadside movement to a more open and relaxed destination feel.`,
    bullets: [
      'Best for families, barkadas, and gear-heavy guests',
      'Most direct and flexible route',
      'Recommended for first-time visitors using Google Maps',
      'Convenient for half-day or full-day trips',
    ],
    cta: 'Drive to Asana',
    href: MAP_LINK,
    testid: 'route-car',
  },
  {
    icon: Ship,
    badge: '~20 min boat',
    duration: 'Short waterside transfer',
    title: 'Arrive by Water from Ubihan Fish Port',
    eyebrow: 'Option 2 · Via Ubihan Fish Port',
    body: `For a more distinctive and memorable arrival, head to the Ubihan fish port drop point (between the tip of Obando and Bulakan), then continue with a short boat ride of about 20 minutes to the Asana area. Instead of going directly from traffic to the resort, you get a transition through the water—something that instantly makes the day feel more special, more local, and more connected to the surrounding fishing landscape.`,
    bullets: [
      'Approximate boat ride: 20 minutes',
      'Ideal for guests who want a more unique arrival',
      'Great for photo moments and first-time visitors',
      'Adds a waterside adventure before the fishing even begins',
    ],
    cta: 'Navigate to Ubihan Fish Port',
    href: 'https://www.google.com/maps/search/?api=1&query=Ubihan+Fish+Port+Obando+Bulacan',
    testid: 'route-ubihan',
  },
  {
    icon: Waves,
    badge: '~25 min river tour',
    duration: 'Scenic river ride',
    title: 'Take the Scenic River Tour Route from Marilao',
    eyebrow: 'Option 3 · Via Constantino Subdivision, Marilao',
    body: `Come through the passenger boat terminal in Constantino Subdivision, Marilao, then continue with a scenic 25-minute river tour ride toward Asana. This route is not just transport—it is an experience in itself, a slow, visual, and highly memorable way to approach the destination through the Bulacan river setting.`,
    bullets: [
      'Approximate river tour ride: 25 minutes',
      'Best for a scenic and unusual arrival story',
      'Great for content creators, families, and experience-seekers',
      'Makes the Asana trip feel like a mini-excursion',
    ],
    cta: 'Navigate to Marilao Terminal',
    href: 'https://www.google.com/maps/search/?api=1&query=Constantino+Subdivision+Marilao+Bulacan+passenger+boat+terminal',
    testid: 'route-marilao',
  },
];

const arrivalStories = [
  {
    label: 'Fast & easy',
    text: 'Go by car for the most direct trip.',
    icon: Car,
  },
  {
    label: 'Short & scenic',
    text: 'Drop at Ubihan Fish Port and take a quick 20-minute boat ride.',
    icon: Ship,
  },
  {
    label: 'Slow & memorable',
    text: 'Start from Constantino Subdivision in Marilao and enjoy a 25-minute river tour approach.',
    icon: Waves,
  },
];

export default function GettingHere() {
  return (
    <div className="App">
      <Navbar />

      {/* Hero */}
      <section
        data-testid="gh-hero"
        className="relative min-h-[88vh] w-full overflow-hidden flex items-end"
      >
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ backgroundImage: `url(${heroBg})` }}
        />
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-b from-pond/40 via-pond/20 to-pond/85"
        />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pb-20 md:pb-28 pt-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className="max-w-3xl"
          >
            <p className="overline text-offwhite/85 mb-6">
              Plan the trip · Choose your route
            </p>
            <h1 className="editorial-display text-offwhite text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              Getting to Asana
              <br />
              <em className="italic font-light text-sky">is part of the adventure.</em>
            </h1>
            <p className="mt-7 text-offwhite/90 text-lg md:text-xl max-w-2xl leading-relaxed">
              Whether you arrive by car, by a short boat ride from Ubihan Fish Port, or by a
              scenic river tour route from Marilao — the journey to Asana is more than a trip,
              it is part of the experience. Just beyond the pace of Metro Manila, Asana offers a
              refreshing transition from city life to still water, open skies, and a uniquely
              memorable outdoor escape.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4 flex-wrap">
              <Button
                as="a"
                href={MAP_LINK}
                target="_blank"
                rel="noreferrer"
                size="lg"
                data-testid="gh-open-maps"
                className="bg-offwhite text-pond hover:bg-sky hover:text-pond"
              >
                Open in Google Maps <ExternalLink className="w-4 h-4" />
              </Button>
              <Button
                as="a"
                href="#routes"
                size="lg"
                variant="secondary"
                data-testid="gh-choose-route"
                className="border-offwhite/70 text-offwhite hover:bg-offwhite hover:text-pond"
              >
                Choose Your Route
              </Button>
              <Button
                as={Link}
                to="/#book"
                size="lg"
                data-testid="gh-book-visit"
                className="bg-pond text-offwhite hover:bg-moss hover:text-pond border border-offwhite/30"
              >
                Book Your Visit
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Reassurance strip */}
      <section className="bg-pond text-offwhite py-5 border-y border-moss/30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 flex flex-wrap gap-x-10 gap-y-3 justify-center md:justify-between items-center text-center md:text-left">
          <span className="overline text-sky">
            A convenient day-trip from Metro Manila
          </span>
          <span className="overline text-offwhite/85">
            Easy-access Bulacan escape · pamilya · barkada · solo
          </span>
          <span className="overline text-offwhite/85">
            Half-day · full-day · weekend ready
          </span>
        </div>
      </section>

      {/* Metro Manila proximity copy */}
      <section className="py-20 md:py-28 bg-sand">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16">
          <p className="overline text-muted mb-4">From the city to the water's edge</p>
          <h2 className="editorial-display text-pond text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
            Close enough to escape,
            <br />
            <em className="italic font-light text-moss">far enough to feel it.</em>
          </h2>
          <p className="mt-8 text-charcoal/85 text-lg md:text-xl leading-relaxed max-w-3xl">
            Asana is close enough for a spontaneous break from Metro Manila, yet far enough to
            feel like a true change of pace. In just one trip, guests shift from urban roads and
            busy schedules to pondside calm, riverside movement, and open-air fishing experiences
            in Bulacan.
          </p>
        </div>
      </section>

      {/* Route options */}
      <section id="routes" data-testid="gh-routes" className="py-20 md:py-28 bg-offwhite">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <p className="overline text-muted mb-4">Three ways to arrive</p>
              <h2 className="editorial-display text-pond text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                Pick the journey
                <br />
                <em className="italic font-light text-moss">that fits your day.</em>
              </h2>
            </div>
            <p className="text-muted md:max-w-sm text-base md:text-lg">
              Each route shapes the mood of the visit. Drive direct, glide in from the fish port,
              or take the long, cinematic river approach.
            </p>
          </div>

          <div className="space-y-6 md:space-y-8">
            {routes.map((r, i) => {
              const Icon = r.icon;
              return (
                <motion.article
                  key={r.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  data-testid={r.testid}
                  className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 rounded-3xl bg-sand border border-moss/25 p-7 md:p-10 hover:border-pond/40 transition-all duration-500"
                >
                  <div className="md:col-span-4 flex flex-col gap-5">
                    <div className="w-16 h-16 rounded-2xl bg-pond text-offwhite flex items-center justify-center">
                      <Icon className="w-8 h-8" strokeWidth={1.4} />
                    </div>
                    <p className="overline text-muted">{r.eyebrow}</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1 bg-pond text-offwhite overline px-3 py-1.5 rounded-full">
                        <Clock className="w-3 h-3" /> {r.badge}
                      </span>
                      <span className="inline-flex items-center bg-offwhite border border-moss/40 text-pond overline px-3 py-1.5 rounded-full">
                        {r.duration}
                      </span>
                    </div>
                  </div>
                  <div className="md:col-span-8">
                    <h3 className="editorial-display text-pond text-3xl md:text-4xl leading-tight mb-4">
                      {r.title}
                    </h3>
                    <p className="text-charcoal/80 text-base md:text-lg leading-relaxed mb-6">
                      {r.body}
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-7">
                      {r.bullets.map((b) => (
                        <li key={b} className="flex gap-3 text-sm text-charcoal/80">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-pond flex-shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                    <a
                      href={r.href}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`${r.testid}-cta`}
                      className="inline-flex items-center gap-2 rounded-full bg-pond text-offwhite hover:bg-moss hover:text-pond px-6 py-3 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5"
                    >
                      {r.cta} <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Map */}
      <section id="map" data-testid="gh-map-section" className="py-20 md:py-28 bg-sand">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div className="max-w-2xl">
              <p className="overline text-muted mb-4">Locate Asana</p>
              <h2 className="editorial-display text-pond text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                The pin sits here.
              </h2>
              <p className="mt-4 text-muted text-base md:text-lg flex items-center gap-2">
                <MapPin className="w-4 h-4 text-pond" />
                <span>
                  {PIN_LAT}, {PIN_LNG} · Sitio Asana, Ubihan, City of Meycauayan, 3020 Bulacan
                </span>
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href={MAP_LINK}
                target="_blank"
                rel="noreferrer"
                data-testid="gh-map-drive"
                className="inline-flex items-center gap-2 rounded-full bg-pond text-offwhite hover:bg-moss hover:text-pond px-5 py-2.5 text-sm font-medium transition-colors"
              >
                Drive to Asana <ExternalLink className="w-4 h-4" />
              </a>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Ubihan+Fish+Port+Obando+Bulacan"
                target="_blank"
                rel="noreferrer"
                data-testid="gh-map-ubihan"
                className="inline-flex items-center gap-2 rounded-full bg-offwhite border border-pond text-pond hover:bg-pond hover:text-offwhite px-5 py-2.5 text-sm font-medium transition-colors"
              >
                To Ubihan Fish Port
              </a>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Constantino+Subdivision+Marilao+Bulacan"
                target="_blank"
                rel="noreferrer"
                data-testid="gh-map-marilao"
                className="inline-flex items-center gap-2 rounded-full bg-offwhite border border-pond text-pond hover:bg-pond hover:text-offwhite px-5 py-2.5 text-sm font-medium transition-colors"
              >
                To Marilao Terminal
              </a>
            </div>
          </div>

          <div className="rounded-[28px] overflow-hidden border border-moss/30 shadow-[0_24px_60px_rgba(26,59,43,0.12)] aspect-[16/10] md:aspect-[16/8] bg-offwhite">
            <iframe
              title="Asana Fishing & Ecological Resorts — Map"
              data-testid="gh-map-iframe"
              src={EMBED_URL}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-full border-0"
            />
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center text-xs text-muted">
            <span className="overline text-pond">Legend</span>
            <span className="inline-flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-pond" /> Car route
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-sky" /> Boat transfer (Ubihan)
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-moss" /> River tour (Marilao)
            </span>
          </div>

          <p className="mt-8 text-xs md:text-sm text-muted max-w-3xl">
            Travel times are approximate and may vary depending on traffic, weather, water
            conditions, and local loading schedules.
          </p>
        </div>
      </section>

      {/* Choose your arrival story */}
      <section data-testid="gh-arrival-story" className="py-20 md:py-28 bg-pond text-offwhite">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="overline text-sky mb-4">Your arrival, your story</p>
            <h2 className="editorial-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Choose how your
              <br />
              <em className="italic font-light text-sky">Asana adventure begins.</em>
            </h2>
            <p className="mt-6 text-offwhite/85 text-lg md:text-xl leading-relaxed">
              Some guests want the easiest direct drive. Others want the fun of arriving by
              boat. Some want the full feeling of leaving the city behind and entering a quieter
              waterside world. At Asana, even the journey can become part of the memory.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {arrivalStories.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="rounded-3xl border border-offwhite/15 p-7 md:p-9 hover:bg-offwhite/5 transition-colors"
                >
                  <Icon className="w-8 h-8 text-sky mb-5" strokeWidth={1.4} />
                  <p className="overline text-sky mb-3">{s.label}</p>
                  <p className="text-offwhite/90 text-lg leading-relaxed">{s.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Storytelling closer */}
      <section className="py-20 md:py-28 bg-offwhite">
        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16 text-center">
          <Compass className="w-10 h-10 text-pond mx-auto mb-6" strokeWidth={1.4} />
          <h2 className="editorial-display text-pond text-3xl md:text-5xl leading-[1.1] tracking-tight">
            Whichever way you come,
            <br />
            <em className="italic font-light text-moss">the water is already waiting.</em>
          </h2>
          <p className="mt-6 text-charcoal/80 text-lg leading-relaxed max-w-2xl mx-auto">
            Tell us how you'd like to arrive — by road, fish port, or river tour — and our team
            will guide you with the right meeting point and timing.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              as={Link}
              to="/#book"
              size="lg"
              data-testid="gh-cta-book"
            >
              Book Your Visit <ArrowRight className="w-4 h-4" />
            </Button>
            <Button
              as={Link}
              to="/#contact"
              size="lg"
              variant="secondary"
              data-testid="gh-cta-contact"
            >
              Talk to our team
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
